'use strict';

var moment = require('moment');
var _ = require('lodash');
var MomentFactory = function MomentFactory(userPreferencesService, MomentConstants) {
  /**
   * Immutable Moment wrapper.
   * Private constructor. Do not use. Use MomentService methods instead.
   *
   * @param {*} config
   * @param {number|date|moment|string} instant
   * @param {string} [stringDateFormat] - required if instant is string
   * @param {boolean} [useStrict=false] - use strict mode for parsing string date value or not
   * @param {boolean} [timezoneApplied] - if set to true, it is assumed that input instant already has some timezone applied
   * @constructor
   */
  var Moment = function Moment(config, instant, stringDateFormat, useStrict, timezoneApplied) {
    this.config = config;
    var date = instant;
    if (_.isDate(instant)) {
      date = cutTimeZoneFromDate(instant);
    }
    this.value = getValue(date, this.config.local, stringDateFormat, useStrict, timezoneApplied);
    this.value.isValid();
  };
  function getDayLightSavingTimeOffset(dateTime, daylightSavingTimeList) {
    if (_.isArray(daylightSavingTimeList) && !_.isEmpty(daylightSavingTimeList)) {
      var dateTimeStr = dateTime.format(MomentConstants.FORMATS.DATE_TIME_ISO_WITHOUT_SECONDS);
      for (var i = 0; i < daylightSavingTimeList.length; i++) {
        var range = daylightSavingTimeList[i];
        // using string comparison to avoid object creation
        if (range.effectiveDate <= dateTimeStr && dateTimeStr < range.discontinueDate) {
          return range.adjustment / 60;
        }
      }
    }
    return 0;
  }
  function getValue(instant, local, stringDateFormat, useStrict, timezoneApplied) {
    if (local) {
      if (moment.isMoment(instant)) {
        instant = instant.clone().utcOffset(0, true);
      }
      return moment.utc(instant, stringDateFormat, useStrict);
    } else {
      var timeZoneData = userPreferencesService.getUserTimeZoneData();
      if (_.isUndefined(timeZoneData)) {
        return moment(instant, stringDateFormat, useStrict);
      } else {
        var utcOffset = timeZoneData.offset / 60;
        var dateTime = moment.utc(instant, stringDateFormat, useStrict).utcOffset(utcOffset, timezoneApplied);
        var dayLightSavingTimeOffset = getDayLightSavingTimeOffset(dateTime, timeZoneData.daylightSavingTimes);
        if (dayLightSavingTimeOffset) {
          return moment.utc(instant, stringDateFormat, useStrict).utcOffset(utcOffset + dayLightSavingTimeOffset, timezoneApplied);
        }
        return dateTime;
      }
    }
  }
  function cutTimeZoneFromDate(date) {
    return [date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()];
  }

  /**
   * Clones the existing Moment object.
   *
   * @returns {Moment}
   */
  Moment.prototype.clone = function () {
    return new Moment(this.config, this.value.valueOf());
  };
  Moment.prototype.isTime = function () {
    return this.config.time || false;
  };

  /**
   *
   * @returns {*}
   */
  Moment.prototype.toISOString = function () {
    return this.value.format(this.config.format);
  };
  Moment.prototype.toString = Moment.prototype.toISOString;

  /**
   *
   * @param {MomentFactory} input
   * @param {string} [units]
   * @returns {boolean}
   */
  Moment.prototype.isSame = function (input, units) {
    return !_.isNil(input) && this.value.isSame(input && input.value, units);
  };

  /**
   *
   * @param {MomentFactory} input
   * @param {string} [units]
   * @returns {*}
   */
  Moment.prototype.isBefore = function (input, units) {
    return !_.isNil(input) && this.value.isBefore(input && input.value, units);
  };

  /**
   *
   * @param {MomentFactory} input
   * @param {string} [units]
   * @returns {*}
   */
  Moment.prototype.isAfter = function (input, units) {
    return !_.isNil(input) && this.value.isAfter(input && input.value, units);
  };

  /**
   *
   * @param {MomentFactory} inputStart
   * @param {MomentFactory} inputEnd
   * @param {string} [units]
   * @param {string} [params]
   * @returns {*}
   */
  Moment.prototype.isBetween = function (inputStart, inputEnd, units, params) {
    return !_.isNil(inputStart) && !_.isNil(inputEnd) && this.value.isBetween(inputStart && inputStart.value, inputEnd && inputEnd.value, units, params);
  };

  /**
   *
   * @param {MomentFactory} input
   * @param units
   * @param floating
   * @returns {number}
   */
  Moment.prototype.diff = function (input, units, floating) {
    return this.value.diff(input && input.value, units, floating);
  };
  Moment.prototype.add = function (term, units) {
    var clone = this.clone();
    clone.value.add(term, units);
    return clone;
  };
  Moment.prototype.subtract = function (term, units) {
    var clone = this.clone();
    clone.value.subtract(term, units);
    return clone;
  };
  Moment.prototype.isValid = function () {
    return this.value.isValid();
  };
  Moment.prototype.valueOf = function () {
    return this.value.valueOf();
  };
  Moment.prototype.set = function (fields) {
    var clone = this.clone();
    clone.value.set(fields);
    return clone;
  };
  Moment.prototype.year = function () {
    return this.value.year();
  };
  Moment.prototype.setYear = function (year) {
    var clone = this.clone();
    clone.value.year(year);
    return clone;
  };
  Moment.prototype.month = function () {
    return this.value.month();
  };
  Moment.prototype.week = function () {
    return this.value.week();
  };
  Moment.prototype.isoWeekOfYear = function () {
    return this.value.isoWeek();
  };
  Moment.prototype.setMonth = function (month) {
    var clone = this.clone();
    clone.value.month(month);
    return clone;
  };
  Moment.prototype.date = function () {
    return this.value.date();
  };
  Moment.prototype.setDate = function (date) {
    var clone = this.clone();
    clone.value.date(date);
    return clone;
  };
  Moment.prototype.day = function () {
    return this.value.day();
  };
  Moment.prototype.setDay = function (day) {
    var clone = this.clone();
    clone.value.day(day);
    return clone;
  };
  Moment.prototype.weekday = function () {
    return this.value.weekday();
  };
  Moment.prototype.hour = function () {
    return this.value.hour();
  };
  Moment.prototype.hours = function () {
    return this.hour();
  };
  Moment.prototype.setHour = function (hour) {
    var clone = this.clone();
    clone.value.hour(hour);
    return clone;
  };
  Moment.prototype.minutes = function () {
    return this.value.minutes();
  };
  Moment.prototype.setMinute = function (minute) {
    var clone = this.clone();
    clone.value.minute(minute);
    return clone;
  };
  Moment.prototype.startOf = function (unit) {
    var clone = this.clone();
    clone.value.startOf(unit);
    return clone;
  };
  Moment.prototype.endOf = function (unit) {
    var clone = this.clone();
    clone.value.endOf(unit);
    return clone;
  };
  Moment.prototype.utcOffset = function (offset) {
    var clone = this.clone();
    clone.value.utcOffset(offset);
    return clone;
  };
  Moment.prototype.format = function (formatString) {
    return this.value.format(formatString);
  };
  return Moment;
};
MomentFactory.$inject = ['UserPreferencesService', 'MomentConstants'];
module.exports = MomentFactory;